import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  Image,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  Linking,
  Platform,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';
import KidInfoModal from './KidInfoModal';
import API_PATHS from '../src/apiCalls/ApiPath';

const typeDescriptionMap = {
  HOMECARE: 'At Home Care',
  LEARNCARE: 'At Learning Center',
  TRANSPORT: 'Transportation',
  BIRTHDAY: 'Birthday Party',
  VENREN: 'Venue Rental',
  TRANSLEARN: 'Learning Care/Transport',
  BREAKFASTCLUB: 'Breakfast Club',
  SOCIALHUB: 'Social Hub',
};

const statusStyles = {
  waiting: {
    color: '#00BFFF',
    backgroundColor: '#00BFFF66',
    label: 'waiting',
  },
  approved: {
    color: '#B7410E',
    backgroundColor: '#B7410E66',
    label: 'approved',
  },
  completed: {
    color: '#556B2F',
    backgroundColor: '#556B2F66',
    label: 'completed',
  },
  cancelled: {
    color: '#964B00',
    backgroundColor: '#964B0066',
    label: 'cancelled',
  },
};

const RecurringDetailsScreen = ({ route, navigation }) => {
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState(null);
  const [selectedKid, setSelectedKid] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [expandedDays, setExpandedDays] = useState({});
  const { recurringId } = route.params;

  const toggleDay = weekDay => {
    setExpandedDays(prev => ({ ...prev, [weekDay]: !prev[weekDay] }));
  };

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const token = await AsyncStorage.getItem('token');
        if (!token) {
          return;
        }
        const response = await axios.get(
          `${API_PATHS.BASE_URL}${API_PATHS.RECURRING_DETAILS}/${recurringId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (response.data.Success === 1) {
          setDetails(response.data.Data);
        } else {
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [recurringId]);

  const timeConverter = epochTime => {
    if (!epochTime) return '';
    const myDate = new Date(epochTime * 1000);
    const day = myDate.getDate().toString().padStart(2, '0');
    const month = myDate.toLocaleString('default', { month: 'short' });
    const year = myDate.getFullYear().toString().slice(2);
    return `${day}-${month}-${year}`;
  };

  function formatEpochTime(epochTime) {
    const date = new Date(epochTime * 1000);
    const options = { hour: '2-digit', minute: '2-digit', hour12: true };

    return date
      .toLocaleString('en-US', options)
      .replace(',', '')
      .replace('at', ',');
  }

  const handleKidPress = kid => {
    setSelectedKid(kid);
    setModalVisible(true);
  };

  function calculatedPrice(firstvalue, secondvalue) {
    const price = firstvalue * secondvalue;
    return price;
  }

  const StatusBadge = ({ status }) => (
    <View
      style={[
        styles.statusBadge,
        { backgroundColor: statusStyles[status]?.backgroundColor || '#E5F7FF' },
      ]}>
      <Text
        style={[
          styles.statusText,
          { color: statusStyles[status]?.color || '#00BFFF' },
        ]}>
        {statusStyles[status]?.label || 'Waiting'}
      </Text>
    </View>
  );

  const shouldShowDetails = status => {
    return !['waiting', 'cancelled'].includes(status);
  };

  function allCapital(input) {
    if (!input || typeof input !== 'string') {
      return input;
    }
    const upperCase = input.toUpperCase();
    return upperCase;
  }

  const shouldShowPayButton = (status, paymentStatus) => {
    if (['waiting', 'cancelled'].includes(status)) return false;
    if (status === 'approved' && paymentStatus === 'PAID') return false;
    return true;
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#A6CE39" />
      </View>
    );
  }

  if (!details) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>Failed to load booking details</Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <Image
            source={require('./assets/back1.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Text style={styles.headerText}>Details</Text>
        <TouchableOpacity onPress={() => navigation.navigate('Notification')}>
          <Image
            source={require('./assets/notification1.png')}
            style={styles.notificationIcon}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.content}>
        {/* Social Hub recurrings send planDetails: null — their plans arrive
            per-weekday in socialHubDetails, so fall back to the booking type. */}
        {details.planDetails ? (
          <Text style={styles.serviceTitle}>
            {typeDescriptionMap[details.planDetails.planService]}
            {':'}
            {details.planDetails.planPackage} - {details.planDetails.planName} (
            {details.planDetails.planNoOfKid}
            -Child){' '}
            {details.planDetails.planType !== '1' &&
              ` - ${details.planDetails.planType}`}
          </Text>
        ) : (
          <Text style={styles.serviceTitle}>
            {typeDescriptionMap[details.recurringDetails.type] ||
              details.recurringDetails.type}
            {details.socialHubDetails?.[0]?.planDetails?.package
              ? `: ${details.socialHubDetails[0].planDetails.package}`
              : ''}
          </Text>
        )}
        <Text style={styles.serviceSubtitle}>
          Starts from: {timeConverter(details.recurringDetails.startDate)}
        </Text>
        <Text style={styles.serviceSubtitle}>
          {details.recurringDetails.bookingType === 'Daily' &&
            details.recurringDetails.planType !== 'Monthly Plan'
            ? ''
            : `Days: ${Object.keys(details.recurringDetails.weekDays).join(', ')}`}
        </Text>
        <StatusBadge status={details.recurringDetails.status} />
        {details.socialHubDetails?.map(dayPlan => {
          const isExpanded = !!expandedDays[dayPlan.weekDay];
          return (
            <View key={dayPlan.weekDay} style={styles.dayCard}>
              <TouchableOpacity
                style={styles.dayCardHeader}
                onPress={() => toggleDay(dayPlan.weekDay)}>
                <Text style={styles.dayCardWeekday}>
                  {isExpanded ? '▾' : '▸'} {dayPlan.weekDay}
                </Text>
                {dayPlan.planDetails?.price && (
                  <Text style={styles.dayCardPrice}>
                    ${dayPlan.planDetails.price}
                  </Text>
                )}
              </TouchableOpacity>

              {isExpanded && (
                <View style={styles.dayCardBody}>
                  {dayPlan.kids?.map((kid, index) => (
                    <TouchableOpacity
                      key={kid.kidId}
                      style={[
                        styles.dayKidRow,
                        index === dayPlan.kids.length - 1 && styles.dayKidRowLast,
                      ]}
                      onPress={() => handleKidPress(kid)}>
                      <Image
                        source={{ uri: kid.profilePic }}
                        style={styles.dayKidAvatar}
                      />
                      <View style={styles.dayKidInfo}>
                        <Text style={styles.dayKidName}>{kid.name}</Text>
                        <Text style={styles.dayKidMeta}>
                          {kid.age}-years, {kid.gender === 'BOY' ? 'Boy' : 'Girl'}
                        </Text>
                      </View>
                      <Text style={styles.dayKidChevron}>›</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              )}
            </View>
          );
        })}

        {/* Social Hub already lists kids inside each weekday card above, so the
            flat all-kids list below is redundant for that booking type. */}
        {details.recurringDetails.type !== 'SOCIALHUB' && details?.kidsDetails?.map(kid => (
          <TouchableOpacity
            key={kid.kidId}
            style={styles.kidCard}
            onPress={() => handleKidPress(kid)}>
            <View style={styles.kidInfo}>
              <Image
                source={{ uri: kid.profilePic }}
                style={styles.kidImage}
              />
              <View style={styles.kidDetails}>
                <Text style={styles.kidName}>{kid.name}</Text>
                <Text style={styles.kidAge}>
                  {kid.age}-years, {kid.gender === 'BOY' ? 'Boy' : 'Girl'}
                </Text>
              </View>
            </View>
            <Image
              source={require('./assets/next1.png')}
              style={styles.nextIcon}
            />
          </TouchableOpacity>
        ))}

        <View style={styles.detailRow}>
          <Text style={[styles.detailLabel, { grid: 1 }]}>Address:</Text>
          {details?.recurringDetails?.addresses?.map((address, index) => (
            <Text
              key={index}
              style={{
                marginTop: 12,
                fontSize: 18,
                color: '#000',
                fontFamily:
                  Platform.OS === 'android'
                    ? 'FacebookSansRegular'
                    : 'Facebook Sans',
                fontStyle: 'normal',
              }}>
              {allCapital(address.tag)} - {address.houseno}, {address.area},{' '}
              {address.city}, {address.zipcode}.
            </Text>
          ))}
        </View>

        <View style={styles.detailRow2}>
          <Text style={styles.detailLabel}>Booking Id:</Text>
          <Text
            style={[
              styles.detailValue,
              {
                color: '#EE3897',
                fontFamily:
                  Platform.OS === 'android'
                    ? 'FacebookSansRegular'
                    : 'Facebook Sans',
                fontStyle: 'normal',
                fontWeight: 'bold',
              },
            ]}>
            {' '}
            {details.recurringDetails.recId}
          </Text>
        </View>
        <View style={styles.detailRow2}>
          <Text style={styles.detailLabel}>Created At:</Text>
          <Text
            style={[
              styles.detailValue,
              {
                color: '#000',
                fontFamily:
                  Platform.OS === 'android'
                    ? 'FacebookSansRegular'
                    : 'Facebook Sans',
                fontStyle: 'normal',
              },
            ]}>
            {' '}
            {timeConverter(details.recurringDetails.createdAt)}
            {', '}
            {formatEpochTime(details.recurringDetails.createdAt)}
          </Text>
        </View>
        {details.recurringDetails.amount !== null && (
          <View style={styles.detailRow2}>
            <Text style={styles.detailLabel}>Charges:</Text>
            <Text
              style={[
                styles.detailValue,
                {
                  color: '#000',
                  fontFamily:
                    Platform.OS === 'android'
                      ? 'FacebookSansRegular'
                      : 'Facebook Sans',
                  fontStyle: 'normal',
                },
              ]}>
              {' $'}
              {details.recurringDetails.amount}
            </Text>
          </View>
        )}

        {details.invoiceDetails !== null && details.invoiceDetails.amount !== null && (
          <View style={styles.detailRow2}>
            <Text style={styles.detailLabel}>Amount:</Text>
            <Text
              style={[
                styles.detailValue,
                {
                  color: '#000',
                  fontFamily:
                    Platform.OS === 'android'
                      ? 'FacebookSansRegular'
                      : 'Facebook Sans',
                  fontStyle: 'normal',
                },
              ]}>
              {' '}
              ${details.invoiceDetails.amount}
            </Text>
          </View>
        )}

        {details?.bookingItems?.map(item => {
          return (
            <View style={styles.detailRow2}>
              <Text style={styles.detailLabel}>{item.title} </Text>
              <Text
                style={[
                  styles.detailValue,
                  {
                    color: '#000',
                    fontFamily:
                      Platform.OS === 'android'
                        ? 'FacebookSansRegular'
                        : 'Facebook Sans',
                    fontStyle: 'normal',
                  },
                ]}>
                {item.quantity} X {'$'}
                {item.price} = {'$'}
                {calculatedPrice(item.quantity, item.price)}
              </Text>
            </View>
          );
        })}

        {details.recurringDetails.status === 'cancelled' && (
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Reason:</Text>
            <Text style={styles.detailValue}>
              {details.recurringDetails.cancelReason}
            </Text>
          </View>
        )}

        {shouldShowDetails(details.recurringDetails.status) && (
          <>
            <View style={styles.detailRow2}>
              <Text style={styles.detailLabel}>Status: </Text>
              <Text
                style={[
                  styles.detailValue,
                  {
                    color:
                      details.recurringDetails.paymentStatus === 'PAID'
                        ? '#4CAF50'
                        : '#FF0000',
                    fontFamily:
                      Platform.OS === 'android'
                        ? 'FacebookSansRegular'
                        : 'Facebook Sans',
                    fontStyle: 'normal',
                    fontWeight: 'bold',
                  },
                ]}>
                {details.recurringDetails.paymentStatus}
              </Text>
            </View>
          </>
        )}

        {!['waiting', 'cancelled'].includes(
          details.recurringDetails.status,
        ) && (
            <View style={styles.buttonContainer}>
              {details.invoiceDetails !== null && (
                <TouchableOpacity
                  onPress={() =>
                    navigation.navigate('Profilebookings', {
                      invoiceId: details.invoiceDetails.invoiceId,
                    })
                  }
                  style={styles.invoiceButton}>
                  <Text style={styles.buttonText}>View Invoice</Text>
                </TouchableOpacity>
              )}

              {shouldShowPayButton(
                details.recurringDetails.status,
                details.recurringDetails.paymentStatus,
              ) && (
                  <TouchableOpacity
                    style={styles.payButton}
                    onPress={() => {
                      // const invoiceLink =
                      //   details?.invoiceDetails?.qbJSON?.Invoice?.InvoiceLink;
                      const invoiceLink = `https://freedommerchants.com/danileeshhmulti.php?inum=${details.invoiceDetails.invoiceId}`;
                      if (invoiceLink) {
                        Linking.openURL(invoiceLink);
                      } else {
                        console.warn('Invoice link is not available');
                      }
                    }}>
                    <Text style={styles.buttonText}>Pay</Text>
                  </TouchableOpacity>
                )}
            </View>
          )}
      </View>

      <KidInfoModal
        kid={selectedKid}
        visible={modalVisible}
        setVisible={setModalVisible}
      />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  errorText: {
    fontSize: 16,
    color: '#FF0000',
    fontFamily:
      Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
    fontStyle: 'normal',
    fontWeight: 'normal',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#A6CE39',
    paddingHorizontal: 15,
    paddingVertical: 15,
  },
  headerText: {
    fontSize: 26,
    color: '#fff',
    fontFamily:
      Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
    fontStyle: 'normal',
    fontWeight: 'bold',
  },
  notificationIcon: {
    width: 25,
    height: 28,
  },
  backIcon: {
    width: 14,
    height: 24,
    tintColor: '#fff',
  },
  content: {
    padding: 20,
  },
  serviceTitle: {
    fontSize: 20,
    fontFamily:
      Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
    fontStyle: 'normal',
    fontWeight: 'bold',
    color: '#1A1A1A',
    marginBottom: 4,
  },
  serviceSubtitle: {
    fontSize: 16,
    color: '#8E8E93',
    marginBottom: 4,
    fontFamily:
      Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
    fontStyle: 'normal',
    fontWeight: 'normal',
  },
  dayCard: {
    backgroundColor: '#F7F9FA',
    borderRadius: 8,
    marginBottom: 12,
    overflow: 'hidden',
  },
  dayCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  dayCardWeekday: {
    fontSize: 16,
    fontFamily:
      Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
    fontStyle: 'normal',
    fontWeight: 'bold',
    color: '#1A1A1A',
  },
  dayCardPrice: {
    fontSize: 16,
    fontFamily:
      Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
    fontStyle: 'normal',
    fontWeight: 'bold',
    color: '#556B2F',
  },
  dayCardBody: {
    paddingHorizontal: 14,
    paddingBottom: 4,
  },
  dayKidRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5EA',
  },
  dayKidRowLast: {
    borderBottomWidth: 0,
  },
  dayKidAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
  },
  dayKidInfo: {
    flex: 1,
    marginLeft: 12,
  },
  dayKidName: {
    fontSize: 15,
    fontFamily:
      Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
    fontStyle: 'normal',
    fontWeight: 'bold',
    color: '#1A1A1A',
  },
  dayKidMeta: {
    fontSize: 13,
    marginTop: 2,
    fontFamily:
      Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
    fontStyle: 'normal',
    fontWeight: 'normal',
    color: '#8E8E93',
  },
  dayKidChevron: {
    fontSize: 20,
    color: '#C7C7CC',
    marginLeft: 8,
  },
  statusBadge: {
    alignSelf: 'flex-start',
    paddingHorizontal: 16,
    paddingVertical: 4,
    borderRadius: 20,
    marginBottom: 16,
  },
  statusText: {
    fontSize: 14,
    fontFamily:
      Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
    fontStyle: 'normal',
    fontWeight: 'bold',
  },
  kidCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  kidInfo: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  kidImage: {
    width: 60,
    height: 60,
    borderRadius: 30,
  },
  kidDetails: {
    marginLeft: 12,
  },
  kidName: {
    fontSize: 18,
    fontFamily:
      Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
    fontStyle: 'normal',
    fontWeight: 'normal',
    color: '#1A1A1A',
  },
  kidAge: {
    fontSize: 14,
    color: '#8E8E93',
    fontFamily:
      Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
    fontStyle: 'normal',
    fontWeight: 'normal',
  },
  nextIcon: {
    width: 14,
    height: 24,
    tintColor: '#666',
  },

  addressLabel: {
    fontSize: 16,
    color: '#8E8E93',
    fontFamily:
      Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
    fontStyle: 'normal',
    fontWeight: 'normal',
    marginBottom: 4,
  },
  addressText: {
    fontSize: 16,
    color: '#1A1A1A',
    fontFamily:
      Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
    fontStyle: 'normal',
    fontWeight: 'normal',
  },
  detailRow: {
    flexDirection: 'column',
    //justifyContent: 'space-between',
    alignItems: 'start',
    marginBottom: 8,
  },
  detailRow2: {
    flexDirection: 'row',
    //justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  detailLabel: {
    fontSize: 18,
    color: '#666',
    fontFamily:
      Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
    fontStyle: 'normal',
    fontWeight: 'normal',
  },
  detailValue: {
    fontSize: 18,
    fontFamily:
      Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
    fontStyle: 'normal',
    fontWeight: 'normal',
    color: '#1A1A1A',
  },
  buttonContainer: {
    alignItems: 'center',
  },
  invoiceButton: {
    backgroundColor: '#A6CE39',
    borderRadius: 1,
    padding: 6,
    alignItems: 'center',
    marginTop: 10,
    width: 150,
    height: 40,
    justifyContent: 'center',
  },
  payButton: {
    backgroundColor: '#A6CE39',
    borderRadius: 1,
    padding: 6,
    alignItems: 'center',
    marginTop: 10,
    width: 150,
    height: 40,
    justifyContent: 'center',
  },
  buttonText: {
    color: '#fff',
    fontSize: 18,
    fontFamily:
      Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
    fontStyle: 'normal',
    fontWeight: 'bold',
  },
});

export default RecurringDetailsScreen;
